// frontend/src/features/onboarding/WizardContext.tsx
import React, { createContext, useContext, useReducer, useCallback, useMemo } from 'react';
import type { OnboardingStep, OnboardingWizardState } from './types';
import { ONBOARDING_STEPS, INITIAL_ONBOARDING_STATE } from './types';

// Action types — next / back / goto / set-field / reset.
type OnboardingAction =
  | { type: 'NEXT_STEP' }
  | { type: 'PREV_STEP' }
  | { type: 'GO_TO_STEP'; step: number }
  | { type: 'SET_FIELD'; updates: Partial<OnboardingWizardState> }
  | { type: 'RESET' };

interface OnboardingWizardContextValue {
  state: OnboardingWizardState;
  next: () => void;
  back: () => void;
  goToStep: (step: number) => void;
  /** Merge a partial state update (e.g. `{ selectedBrands }`, `{ dosing }`). */
  setField: (updates: Partial<OnboardingWizardState>) => void;
  reset: () => void;
  // Computed values
  isFirstStep: boolean;
  isLastStep: boolean;
  currentStepInfo: OnboardingStep;
}

const MAX_STEP = ONBOARDING_STEPS.length - 1;

function onboardingReducer(
  state: OnboardingWizardState,
  action: OnboardingAction
): OnboardingWizardState {
  switch (action.type) {
    case 'NEXT_STEP':
      return { ...state, currentStep: Math.min(state.currentStep + 1, MAX_STEP) };

    case 'PREV_STEP':
      return { ...state, currentStep: Math.max(state.currentStep - 1, 0) };

    case 'GO_TO_STEP':
      return { ...state, currentStep: Math.max(0, Math.min(action.step, MAX_STEP)) };

    case 'SET_FIELD':
      return { ...state, ...action.updates };

    case 'RESET':
      return INITIAL_ONBOARDING_STATE;

    default:
      return state;
  }
}

const OnboardingWizardContext = createContext<OnboardingWizardContextValue | undefined>(
  undefined
);

export function OnboardingWizardProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(onboardingReducer, INITIAL_ONBOARDING_STATE);

  const next = useCallback(() => dispatch({ type: 'NEXT_STEP' }), []);
  const back = useCallback(() => dispatch({ type: 'PREV_STEP' }), []);
  const goToStep = useCallback((step: number) => dispatch({ type: 'GO_TO_STEP', step }), []);
  const setField = useCallback(
    (updates: Partial<OnboardingWizardState>) => dispatch({ type: 'SET_FIELD', updates }),
    []
  );
  const reset = useCallback(() => dispatch({ type: 'RESET' }), []);

  const isFirstStep = state.currentStep === 0;
  const isLastStep = state.currentStep === MAX_STEP;
  const currentStepInfo =
    ONBOARDING_STEPS[state.currentStep] ?? (ONBOARDING_STEPS[0] as OnboardingStep);

  const value = useMemo<OnboardingWizardContextValue>(
    () => ({
      state,
      next,
      back,
      goToStep,
      setField,
      reset,
      isFirstStep,
      isLastStep,
      currentStepInfo,
    }),
    // Action functions are stable (useCallback with []); only state-derived values change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state, isFirstStep, isLastStep, currentStepInfo]
  );

  return (
    <OnboardingWizardContext.Provider value={value}>
      {children}
    </OnboardingWizardContext.Provider>
  );
}

export function useOnboardingWizard(): OnboardingWizardContextValue {
  const context = useContext(OnboardingWizardContext);
  if (!context) {
    throw new Error('useOnboardingWizard must be used within OnboardingWizardProvider');
  }
  return context;
}
